import React, { FunctionComponent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ServiceLocation } from 'src/api/@generated/backend';

import { ChooseButton } from 'src/components/choose-button';
import { ChooseContainer } from 'src/components/choose-container';
import { Image } from 'src/components/image';
import { i18n } from 'src/i18n';
import { ServiceTypeMapper } from 'src/mapper/service-type-mapper';
import { houseImageProps } from 'src/resources/images';
import { NavigationService } from 'src/services/navigation-service';
import { selectAddressActionCreator } from 'src/state/address/action-creators/select-address-action-creator';
import { setAddress } from 'src/state/address/address-slice';
import { selectIsAddressSelected, selectServiceLocationsOrThrow } from 'src/state/address/selectors';
import { getRequest } from 'src/state/request/selectors';
import { RequestStatus, RequestType } from 'src/state/request/types';
import { useAppDispatch, useAppSelector } from 'src/state/thunk-api';
import {
  StyledContentContainer,
  StyledDwellingUnitNumber,
  StyledImageContainer,
  StyledTextContainer,
  StyledTitle,
} from 'src/pages/choose-service-location/choose-service-location.styled';

export const PageChooseServiceLocation: FunctionComponent = () => {
  const serviceLocations = useAppSelector(selectServiceLocationsOrThrow);
  const accountInfoRequest = useAppSelector(getRequest(RequestType.GET_ACCOUNT_INFO));
  const serviceLocationSelected = useAppSelector(selectIsAddressSelected);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      accountInfoRequest.status === RequestStatus.SUCCESS &&
      serviceLocations.length === 1 &&
      !serviceLocationSelected
    ) {
      dispatch(setAddress(serviceLocations[0].siteId));
      navigate(NavigationService.STORE, { replace: true });
    }
  }, [accountInfoRequest, serviceLocations, serviceLocationSelected, dispatch, navigate]);

  const renderTitle = (serviceLocation: ServiceLocation) => {
    const {
      address: { streetAddress, dwellingUnitNumber },
    } = serviceLocation;

    return (
      <StyledContentContainer>
        <StyledImageContainer>
          <Image {...houseImageProps} />
        </StyledImageContainer>
        <StyledTextContainer>
          <StyledTitle variant="paragraph2" component="p" data-hj-suppress={true}>
            {streetAddress}
          </StyledTitle>
          {dwellingUnitNumber && (
            <StyledDwellingUnitNumber variant="paragraph3" component="p" data-hj-suppress={true}>
              {dwellingUnitNumber}
            </StyledDwellingUnitNumber>
          )}
        </StyledTextContainer>
      </StyledContentContainer>
    );
  };

  const renderServiceLocationButton = (serviceLocation: ServiceLocation) => {
    const { services, siteId } = serviceLocation;

    return (
      <ChooseButton
        key={siteId}
        title={renderTitle(serviceLocation)}
        items={services.map(ServiceTypeMapper.toSelectButtonItem)}
        onClick={() => {
          dispatch(selectAddressActionCreator(siteId));
          navigate(NavigationService.STORE);
        }}
      />
    );
  };

  return (
    <ChooseContainer
      title={i18n.page.chooseServiceLocation.title}
      subtitle={i18n.page.chooseServiceLocation.description}
    >
      {serviceLocations.map(renderServiceLocationButton)}
    </ChooseContainer>
  );
};
